import { useMemo, useState } from 'react';
import { BookOpen, FileText, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/ui/empty-state';
import PatientLayout from '@/components/patient/PatientLayout';
import { ActivityFeedItem } from '@/components/timeline/ActivityFeedItem';
import { TimelineDayGroup } from '@/components/timeline/TimelineDayGroup';
import { getCalendarBadgeParts } from '@/lib/date';
import type { ActivityEvent } from '@/lib/activityFeed';
import { groupActivityEvents } from '@/lib/activityFeed';

type Charts = {
  presenceHeatmap: Array<{ date: string; count: number; active?: boolean }>;
  moodTrend: Array<{ date: string; mood: number | null }>;
};
type Stats = {
  activityRate: number;
  totalChats: number;
  totalDiaries: number;
  lastActivityAt: string | null;
};
type Patient = {
  id: number;
  name: string;
  email?: string | null;
  demo?: boolean;
  disabledAt?: string | null;
  emovidocEnabled?: boolean;
};
type Crumb = { text: string; url?: string; current?: boolean };
type PushTemplate = { key: string; title: string; body: string };
type PushNotifications = {
  hasActiveDevice: boolean;
  templates: PushTemplate[];
  history?: Array<{
    id: number;
    title: string;
    body?: string | null;
    status: 'queued' | 'sent' | 'failed';
    sentAt: string;
  }>;
};
type Props = {
  patient: Patient;
  stats: Stats;
  charts: Charts;
  timeline: ActivityEvent[];
  breadcrumbs: Crumb[];
  pushNotifications: PushNotifications;
};

export type PageProps = Props;

const TIMELINE_FILTERS = [
  { key: 'all', label: 'Tutto' },
  { key: 'chat', label: 'Chat' },
  { key: 'journal', label: 'Diari' },
] as const;

function formatDateShort(dateString?: string | null) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

const MOOD_COLORS: Record<number, string> = {
  1: '#ef4444',
  2: '#f97316',
  3: '#eab308',
  4: '#84cc16',
  5: '#22c55e',
  6: '#10b981',
  7: '#14b8a6',
  8: '#06b6d4',
  9: '#0ea5e9',
  10: '#3b82f6',
};

function PresenceHeatmap({ data }: { data: Charts['presenceHeatmap'] }) {
  const hasActivity = data.some((d) => (d.count || 0) > 0);

  if (!data.length || !hasActivity) {
    return (
      <EmptyState
        title="Nessun dato di presenza"
        description="Compila chat o diari per vedere la heatmap"
        icon={<FileText className="h-6 w-6" aria-hidden />}
        variant="contrast"
        className="py-12"
      />
    );
  }

  const maxCount = Math.max(...data.map((d) => d.count || 0), 1);

  // Align to weeks starting Monday
  const cells = (() => {
    const parsed = data.map((d) => ({ ...d, dateObj: new Date(d.date) }));
    const startWeekday = (parsed[0].dateObj.getDay() + 6) % 7; // Monday=0
    const paddedStart = Array.from({ length: startWeekday }).map((_, idx) => ({
      date: `pad-start-${idx}`,
      count: 0,
      empty: true,
    }));
    const padded = [...paddedStart, ...parsed];
    const endPad = (7 - (padded.length % 7)) % 7;
    const paddedEnd = Array.from({ length: endPad }).map((_, idx) => ({
      date: `pad-end-${idx}`,
      count: 0,
      empty: true,
    }));
    return [...padded, ...paddedEnd];
  })();

  const weeks = cells.length / 7;

  const bucketClass = (count: number, empty?: boolean) => {
    if (empty) return 'bg-transparent border-transparent';
    if (count === 0)
      return 'bg-slate-100 border-slate-200 dark:bg-slate-900/50 dark:border-slate-800';
    const ratio = count / maxCount;
    if (ratio >= 0.75) return 'bg-primary text-primary-foreground border-primary';
    if (ratio >= 0.5) return 'bg-primary/70 border-primary/70';
    if (ratio >= 0.25) return 'bg-primary/50 border-primary/60';
    return 'bg-primary/30 border-primary/40';
  };

  const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[auto_1fr] gap-3">
        <div className="flex flex-col items-end justify-between text-xs text-muted-foreground pt-1 gap-2">
          {weekdayLabels.map((w) => (
            <span key={w} className="h-5 leading-5">
              {w}
            </span>
          ))}
        </div>
        <div
          className="grid gap-1"
          style={{
            gridTemplateColumns: `repeat(${weeks}, minmax(0, 24px))`,
            gridTemplateRows: 'repeat(7, minmax(0, 1fr))',
          }}
          role="list"
          aria-label="Presenza ultimi 60 giorni"
        >
          {cells.map((cell, idx) => (
            <div
              key={cell.date}
              role="listitem"
              title={
                'empty' in cell && cell.empty
                  ? ''
                  : `${formatDateShort(cell.date)} - ${cell.count > 0 ? `${cell.count} attività` : 'Nessuna attività'}`
              }
              className={cn(
                'h-5 w-5 rounded-sm border transition-colors',
                bucketClass(cell.count, 'empty' in cell && cell.empty),
                'mx-auto my-[2px]',
              )}
              style={{ gridRow: (idx % 7) + 1, gridColumn: Math.floor(idx / 7) + 1 }}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        Meno
        <span className="inline-block h-4 w-4 rounded-sm border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900/50" />{' '}
        <span className="inline-block h-4 w-4 rounded-sm border border-primary/40 bg-primary/30" />
        <span className="inline-block h-4 w-4 rounded-sm border border-primary/60 bg-primary/50" />
        <span className="inline-block h-4 w-4 rounded-sm border border-primary/70 bg-primary/70" />
        <span className="inline-block h-4 w-4 rounded-sm border border-primary bg-primary" /> Più
      </div>
    </div>
  );
}

export default function Show({ patient, stats, charts, timeline }: PageProps) {
  const [filter, setFilter] = useState<(typeof TIMELINE_FILTERS)[number]['key']>('all');
  const [search, setSearch] = useState('');
  const filteredTimeline = useMemo(() => {
    const byType = filter === 'all' ? timeline : timeline.filter((i) => i.kind === filter);
    if (!search.trim()) return byType;
    const term = search.toLowerCase();
    return byType.filter(
      (item) =>
        item.title.toLowerCase().includes(term) ||
        (item.summary || '').toLowerCase().includes(term),
    );
  }, [filter, timeline, search]);

  const timelineEntries = useMemo(() => filteredTimeline, [filteredTimeline]);
  const groupedTimeline = useMemo(() => groupActivityEvents(timelineEntries), [timelineEntries]);
  const moodTrendFiltered = useMemo(
    () => charts.moodTrend.filter((m) => (m.mood || 0) > 0),
    [charts.moodTrend],
  );
  return (
    <PatientLayout
      patient={patient}
      stats={stats}
      charts={charts}
      activeTab="dashboard"
    >
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Andamento Presenza</CardTitle>
              <CardDescription>
                % giorni con attività (chat o diario) · ultimi 2 mesi
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PresenceHeatmap data={charts.presenceHeatmap} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Andamento Umore</CardTitle>
            </CardHeader>
            <CardContent>
              {!moodTrendFiltered.length && (
                <EmptyState
                  title="Nessun dato di umore disponibile"
                  description="Compila qualche diario per vedere il trend"
                  variant="contrast"
                  className="border-dashed py-10"
                />
              )}
              {moodTrendFiltered.length > 0 && (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={moodTrendFiltered}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} vertical={false} />
                    <XAxis dataKey="date" tickFormatter={formatDateShort} tickLine={false} />
                    <YAxis domain={[0, 10]} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value) => {
                        const num = typeof value === 'number' ? value : null;
                        return num === null ? ['N/A', 'Mood'] : [`${num}/10`, 'Mood'];
                      }}
                      labelFormatter={(v) => formatDateShort(String(v))}
                    />
                    <Bar dataKey="mood" radius={[4, 4, 0, 0]} minPointSize={2}>
                      {moodTrendFiltered.map((entry, index) => {
                        const mood = entry.mood ?? 0;
                        const bucket = MOOD_COLORS[mood] || MOOD_COLORS[1];
                        return <Cell key={`cell-${index}`} fill={bucket} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>Attività recenti</CardTitle>
                <CardDescription>{timelineEntries.length} attività</CardDescription>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                <div className="flex gap-2">
                  {TIMELINE_FILTERS.map((f) => {
                    const icon =
                      f.key === 'chat' ? (
                        <MessageSquare className="h-4 w-4" />
                      ) : f.key === 'journal' ? (
                        <BookOpen className="h-4 w-4" />
                      ) : null;
                    return (
                      <Button
                        key={f.key}
                        variant={filter === f.key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter(f.key)}
                        className={icon ? 'gap-2' : undefined}
                      >
                        {icon}
                        {f.label}
                      </Button>
                    );
                  })}
                </div>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca per titolo o testo"
                  className="h-9 w-full max-w-xs"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!timelineEntries.length && (
              <div className="text-center text-sm text-muted-foreground">
                Nessuna attività nel periodo.
              </div>
            )}
            {timelineEntries.length > 0 && (
              <div className="relative grid grid-cols-1 gap-6">
                {groupedTimeline.map((group) => {
                  const badge = getCalendarBadgeParts(group.date);
                  const dayNumber = badge?.day ?? '--';
                  const monthLabel = badge?.month ?? '—';
                  return (
                    <TimelineDayGroup
                      key={group.key}
                      day={dayNumber}
                      month={monthLabel}
                      contentClassName="space-y-2"
                    >
                      {group.items.map((item) => (
                        <ActivityFeedItem key={item.id} event={item} />
                      ))}
                    </TimelineDayGroup>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PatientLayout>
  );
}
